/**
 * Jaipur Handloom AE8 component owner
 * File: jh-30-home-product.js
 * Owns: catalog sales ordering, product-media zoom, horizontal rail controls,
 *       behavioral context and the exit-intent dialog.
 * Depends on: jh-00-core.js (window.JH.Utils and window.JH.A11y).
 * Do not add duplicate carousel, product-option, navigation or cart handlers here.
 */
(function() {
  'use strict';

  var JH = window.JH = window.JH || {};
  var U = JH.Utils;
  var A11y = JH.A11y;
  var state = {
    behaviorBound: false,
    exitBound: false,
    exitOpener: null
  };

  function qs(selector, root) {
    return U ? U.qs(selector, root) : (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return U ? U.qsa(selector, root) : Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function escapeHtml(value) {
    return U ? U.escapeHtml(value) : String(value == null ? '' : value).replace(/[&<>"']/g, '');
  }

  function numberAttribute(element, name) {
    var number = parseInt(element.getAttribute(name) || '0', 10);
    return isNaN(number) ? 0 : number;
  }

  function readStore(key, fallback) {
    try {
      return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
    } catch (error) {
      return fallback;
    }
  }

  function writeStore(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  }

  function sortCollectionCardsBySales(scope) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      params = null;
    }
    var requestedSort = params ? params.get('sort_by') : '';
    if (requestedSort && requestedSort !== 'best-selling') return;

    qsa('.jh-collection-products-v1314 [data-jh-product-grid]', scope || document).forEach(function(grid) {
      if (grid.dataset.jhSalesSorted === 'true') return;
      var cards = qsa('[data-jh-product-card]', grid);
      if (cards.length < 2) return;
      var hasSales = cards.some(function(card) {
        return numberAttribute(card, 'data-jh-sold-count') > 0;
      });
      if (!hasSales) return;

      cards.forEach(function(card, index) {
        card.dataset.jhOriginalIndex = String(index);
      });
      cards.sort(function(a, b) {
        return numberAttribute(b, 'data-jh-sold-count') - numberAttribute(a, 'data-jh-sold-count') ||
          numberAttribute(a, 'data-jh-original-index') - numberAttribute(b, 'data-jh-original-index');
      }).forEach(function(card) {
        grid.appendChild(card);
      });
      grid.dataset.jhSalesSorted = 'true';
    });
  }

  function keepProductDetailsPlain(scope) {
    qsa('.jh-product .jh-accordion strong,.jh-product .jh-accordion b,.jh-product .jh-rte strong,.jh-product .jh-rte b,.jh-product .jh-accordion [style*="font-weight"],.jh-product .jh-rte [style*="font-weight"]', scope || document).forEach(function(element) {
      element.style.fontWeight = '400';
    });
  }

  function initProductZoom(scope) {
    qsa('[data-jh-main-media]', scope || document).forEach(function(media) {
      if (media.dataset.jhZoomReady === 'true') return;
      media.dataset.jhZoomReady = 'true';

      function canZoom() {
        return Boolean(qs('img', media) && !qs('video,iframe,model-viewer', media));
      }

      media.addEventListener('pointerenter', function() {
        if (window.matchMedia('(hover:hover)').matches && canZoom()) media.classList.add('is-zooming');
      });
      media.addEventListener('pointermove', function(event) {
        if (!media.classList.contains('is-zooming')) return;
        var rect = media.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100;
        var y = ((event.clientY - rect.top) / Math.max(1, rect.height)) * 100;
        media.style.setProperty('--jh-zoom-x', Math.max(0, Math.min(100, x)).toFixed(2) + '%');
        media.style.setProperty('--jh-zoom-y', Math.max(0, Math.min(100, y)).toFixed(2) + '%');
      });
      ['pointerleave', 'pointercancel', 'touchstart'].forEach(function(eventName) {
        media.addEventListener(eventName, function() {
          media.classList.remove('is-zooming');
          media.style.removeProperty('--jh-zoom-x');
          media.style.removeProperty('--jh-zoom-y');
        }, { passive: true });
      });
    });
  }

  function bindScrollableRail(section, row, previous, next, stepRatio) {
    if (!row || section.dataset.jhScrollReady === 'true') return;
    section.dataset.jhScrollReady = 'true';

    function step(direction) {
      row.scrollBy({
        left: direction * Math.max(260, Math.round(row.clientWidth * stepRatio)),
        behavior: 'smooth'
      });
    }

    if (previous) previous.addEventListener('click', function(event) {
      event.preventDefault();
      step(-1);
    });
    if (next) next.addEventListener('click', function(event) {
      event.preventDefault();
      step(1);
    });
  }

  function initScrollControls(scope) {
    qsa('[data-jh-scroll-section]', scope || document).forEach(function(section) {
      bindScrollableRail(
        section,
        qs('[data-jh-scroll-row]', section),
        qs('[data-jh-scroll-prev]', section),
        qs('[data-jh-scroll-next]', section),
        0.86
      );
    });

    qsa('[data-jh-hero-gallery-v13134="true"]', scope || document).forEach(function(section) {
      bindScrollableRail(
        section,
        qs('[data-jh-hero-gallery-track]', section),
        qs('[data-jh-hero-gallery-prev]', section),
        qs('[data-jh-hero-gallery-next]', section),
        0.86
      );
    });
  }

  function pushBehavior(list, item, maximum) {
    list = list || [];
    if (!item || !item.url) return list;
    list = list.filter(function(existing) { return existing.url !== item.url; });
    item.time = new Date().toISOString();
    list.unshift(item);
    return list.slice(0, maximum || 12);
  }

  function behaviorSummary() {
    var behavior = readStore('jh_behavior', { views: [], cards: [], collections: [], cart: [] });
    var pieces = [];
    if (behavior.cart && behavior.cart.length) {
      pieces.push('Cart: ' + behavior.cart.map(function(item) {
        return item.title + ' x' + item.qty;
      }).join(' | '));
    }
    if (behavior.views && behavior.views.length) {
      pieces.push('Viewed: ' + behavior.views.slice(0, 5).map(function(item) {
        return item.title + ' ' + item.url;
      }).join(' | '));
    }
    if (behavior.collections && behavior.collections.length) {
      pieces.push('Collections: ' + behavior.collections.slice(0, 4).map(function(item) {
        return item.title + ' ' + item.url;
      }).join(' | '));
    }
    return pieces.join('\n');
  }

  function updateBehaviorNotes() {
    qsa('[data-jh-behavior-note]').forEach(function(input) {
      input.value = behaviorSummary().slice(0, 950);
    });
  }

  function trackCurrentPage() {
    var behavior = readStore('jh_behavior', { views: [], cards: [], collections: [], cart: [] });
    var productJson = qs('[data-jh-product-json]');
    if (productJson) {
      try {
        var product = JSON.parse(productJson.textContent || '{}');
        var image = typeof product.featured_image === 'string' ? product.featured_image :
          (product.featured_image && product.featured_image.src ? product.featured_image.src : '');
        behavior.views = pushBehavior(behavior.views, {
          title: product.title,
          url: window.location.pathname,
          image: image,
          type: product.type || ''
        }, 16);
      } catch (error) {}
    }
    var collectionTitle = qs('.jh-collection h1');
    if (collectionTitle) {
      behavior.collections = pushBehavior(behavior.collections, {
        title: collectionTitle.textContent.trim(),
        url: window.location.pathname,
        image: '',
        type: 'collection'
      }, 10);
    }
    writeStore('jh_behavior', behavior);
    updateBehaviorNotes();
  }

  function initBehaviorTracking() {
    trackCurrentPage();
    if (state.behaviorBound) return;
    state.behaviorBound = true;
    document.addEventListener('click', function(event) {
      var card = event.target.closest && event.target.closest('[data-jh-product-card]');
      if (!card) return;
      var link = qs('a[href*="/products/"]', card);
      var url = card.getAttribute('data-jh-track-product-url') || (link ? link.getAttribute('href') : '');
      if (!url) return;
      var behavior = readStore('jh_behavior', { views: [], cards: [], collections: [], cart: [] });
      behavior.cards = pushBehavior(behavior.cards, {
        title: card.getAttribute('data-jh-track-product-title') || '',
        url: url,
        image: card.getAttribute('data-jh-track-product-image') || '',
        type: card.getAttribute('data-jh-track-product-type') || ''
      }, 16);
      writeStore('jh_behavior', behavior);
    });
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') updateBehaviorNotes();
    });
  }

  function renderExitRecent() {
    var box = qs('[data-jh-exit-recent]');
    var list = qs('[data-jh-exit-recent-list]');
    if (!box || !list) return;
    var behavior = readStore('jh_behavior', { views: [], cards: [] });
    var items = (behavior.views || []).concat(behavior.cards || []).filter(function(item) {
      return item && item.url;
    }).slice(0, 3);
    if (!items.length) {
      box.hidden = true;
      return;
    }
    list.innerHTML = items.map(function(item) {
      return '<a href="' + escapeHtml(item.url) + '">' +
        (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" decoding="async">' : '<span></span>') +
        '<span>' + escapeHtml(item.title || 'View product') + '</span></a>';
    }).join('');
    box.hidden = false;
  }

  function closeExitIntent() {
    var popup = qs('[data-jh-exit-intent]');
    if (!popup || popup.hidden) return;
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    if (A11y) A11y.deactivateDialog(popup, state.exitOpener);
    state.exitOpener = null;
  }

  function showExitIntent(reason) {
    var popup = qs('[data-jh-exit-intent]');
    if (!popup || popup.dataset.jhShown === 'true') return;
    try {
      if (localStorage.getItem('jh_exit_shown_once') === '1' || sessionStorage.getItem('jh_exit_shown') === '1') return;
    } catch (error) {}

    var behavior = readStore('jh_behavior', { views: [], cards: [], cart: [] });
    var useful = reason === 'cart-close' ||
      (behavior.cart && behavior.cart.length) ||
      (behavior.views && behavior.views.length > 1) ||
      (behavior.cards && behavior.cards.length > 1);
    if (!useful && reason !== 'mouse-exit') return;

    state.exitOpener = document.activeElement;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    popup.dataset.jhShown = 'true';
    try {
      sessionStorage.setItem('jh_exit_shown', '1');
      localStorage.setItem('jh_exit_shown_once', '1');
    } catch (error) {}
    renderExitRecent();
    updateBehaviorNotes();
    if (A11y) A11y.activateDialog(popup, state.exitOpener, qs('[data-jh-exit-close]', popup));
  }

  function initExitIntent() {
    if (state.exitBound || !qs('[data-jh-exit-intent]')) return;
    state.exitBound = true;
    document.addEventListener('click', function(event) {
      if (event.target.closest && event.target.closest('[data-jh-exit-close]')) {
        event.preventDefault();
        closeExitIntent();
      }
    });
    document.addEventListener('keydown', function(event) {
      var popup = qs('[data-jh-exit-intent]');
      if (!popup || popup.hidden) return;
      if (event.key === 'Escape') closeExitIntent();
      else if (event.key === 'Tab' && A11y) A11y.trapTab(event, popup);
    });
    document.addEventListener('mouseout', function(event) {
      if (event.clientY <= 0 && !event.relatedTarget) showExitIntent('mouse-exit');
    });
    window.setTimeout(function() { showExitIntent('timed-engagement'); }, 42000);
  }

  function init(scope) {
    document.documentElement.classList.add('jh-v1330-header-recovery');
    sortCollectionCardsBySales(scope);
    keepProductDetailsPlain(scope);
    initProductZoom(scope);
    initScrollControls(scope);
    initBehaviorTracking();
    initExitIntent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(document); }, { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function(event) {
    window.setTimeout(function() { init(event.target); }, 80);
  });
  document.addEventListener('jh:collection:updated', function() {
    qsa('.jh-collection-products-v1314 [data-jh-product-grid]').forEach(function(grid) {
      grid.dataset.jhSalesSorted = '';
    });
    window.setTimeout(function() { sortCollectionCardsBySales(document); }, 80);
  });

  JH.ExitIntent = { open: showExitIntent, close: closeExitIntent };
})();
